import Multiselect from '../../Multiselect'
import { useUIComponentsContext } from '../../Provider/UiComponentsProvider'
import { useAccessContext } from '../AccessProvider'
import ContributorContainer from '../Components/ContributorContainer'
import GuestBadge from '../Components/GuestBadge'
import TeamContributorCard from '../Components/TeamContributorCard'
import UserContributor from '../Components/UserContributor'
import UserDropdown from '../Components/UserDropdown'
import useContributorActions from '../hooks/useContributorActions'
import { AccessAccount } from '../types'
import { CONTRIBUTOR_RIGHTS } from '../utils'

export interface ISharedTab {
  isOwnerEditable?: boolean
  resourceOwner?: AccessAccount
  ownerTitle?: string
  usersTitle?: string
  onOwnerChange?: (newOwner: AccessAccount) => void
}

const SharedTab = ({
  isOwnerEditable = true,
  ownerTitle,
  usersTitle,
  resourceOwner,
  onOwnerChange
}: ISharedTab) => {
  const { t } = useUIComponentsContext()
  const { userContributors, teamContributors, teams } = useAccessContext()

  ownerTitle = ownerTitle || t?.('access.owner')

  usersTitle = usersTitle || t?.('access.users')

  const { OWNER } = CONTRIBUTOR_RIGHTS(t)
  const { handleAddUser, ...userActions } = useContributorActions(
    'User',
    resourceOwner as AccessAccount
  )
  const projectResponsible = resourceOwner
    ? userContributors?.find((c) => c?.account.id === resourceOwner?.id)
    : null

  const { handleAddTeam, onTeamDelete, ...teamActions } = useContributorActions(
    'Team',
    resourceOwner as AccessAccount
  )

  const restUserContributors = resourceOwner
    ? userContributors?.filter((c) => c?.account.id !== resourceOwner?.id)
    : userContributors

  return (
    <>
      {resourceOwner && (
        <ContributorContainer
          title={ownerTitle}
          containerClassName="bg-oxford-gray-50"
          contributors={
            <UserContributor
              key={resourceOwner?.id}
              account={resourceOwner}
              access={OWNER.value}
              onOwnerChange={onOwnerChange}
              disabled={!isOwnerEditable}
            />
          }
        />
      )}

      {projectResponsible && (
        <ContributorContainer
          title={t?.('access.owner')}
          contributors={
            <UserContributor
              key={projectResponsible?.account?.id}
              account={projectResponsible.account}
              disabled
              access={projectResponsible.access}
              isProjectResponsible
            />
          }
        />
      )}

      {!!teamContributors?.length && (
        <ContributorContainer
          title={t?.('access.teams')}
          contributors={teamContributors.map((teamContributor) => (
            <TeamContributorCard
              key={teamContributor.team.id}
              teamContributor={teamContributor}
              onTeamDelete={onTeamDelete}
              projectResponsibleId={projectResponsible?.account?.id}
              resourceOwnerId={resourceOwner?.id}
              {...teamActions}
            />
          ))}
        />
      )}

      <div className="py-3">
        <div className="text-oxford-gray-400 uppercase mb-2 text-xs">
          {usersTitle}
        </div>
        {!!restUserContributors?.length && (
          <div className="pb-3 flex flex-col gap-3">
            {restUserContributors.map(
              ({ account, access, locked, removable }) => (
                <UserContributor
                  key={account.id}
                  account={account}
                  access={access}
                  disableAccessDropdown={locked}
                  showMoreActions={removable !== false}
                  {...userActions}
                />
              )
            )}
          </div>
        )}

        <div className="flex justify-between w-100">
          <div className="flex-grow pr-2">
            <Multiselect
              data={teams}
              placeholder={`+ ${t?.('buttons.addTeam')}`}
              onChange={handleAddTeam}
              dataParser={(data) =>
                data.filter(
                  (t) => !teamContributors?.find(({ team }) => team.id === t.id)
                )
              }
              renderItem={({ item }) => (
                <div className="flex items-center justify-between p-3">
                  <span className="text-sm truncate flex-1">{item.name}</span>
                  {item.isGuest && <GuestBadge />}
                </div>
              )}
            />
          </div>

          <div className="flex-grow pl-2">
            <UserDropdown onChange={handleAddUser} resourceOwner={resourceOwner} />
          </div>
        </div>
      </div>
    </>
  )
}

export default SharedTab
